import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import WatchCTA from '#app/routes/resources+/watch-cta.tsx'
import {
	connections,
	dungeons,
	races,
	recordedRuns,
	zones,
	type ZoneConnection,
} from './__data.ts'

export const meta = () => {
	const title = 'JaseOwns - EverQuest Legends - Dungeon Runs'
	const description =
		'Pick a race, get the zone-by-zone path from your starting city to every classic dungeon. Fresh character dungeon runs, live on stream.'
	return [
		{ title: title },
		{ description: description },
		{
			name: 'keywords',
			content:
				'everquest legends,everquest,dungeon runs,leveling,starting city,zone path,norrath',
		},
		{ property: 'twitter:card', content: 'summary' },
		{ property: 'twitter:creator', content: '@jaseowns' },
		{ property: 'twitter:site', content: '@jaseowns' },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
	]
}

const zoneName = (id: string) =>
	zones.find((z) => z.id === id)?.name ?? id

function buildAdjacency() {
	const adjacency = new Map<string, { to: string; connection: ZoneConnection }[]>()
	for (const connection of connections) {
		for (const [from, to] of [
			[connection.from, connection.to],
			[connection.to, connection.from],
		] as const) {
			const list = adjacency.get(from) ?? []
			list.push({ to, connection })
			adjacency.set(from, list)
		}
	}
	return adjacency
}

type PathLeg = { zoneId: string; connection?: ZoneConnection }

function findPath(startZoneId: string, endZoneId: string): PathLeg[] | null {
	const adjacency = buildAdjacency()
	const previous = new Map<string, PathLeg>()
	const visited = new Set([startZoneId])
	const queue = [startZoneId]
	while (queue.length) {
		const current = queue.shift()!
		if (current === endZoneId) {
			const path: PathLeg[] = []
			let cursor = endZoneId
			while (cursor !== startZoneId) {
				const leg = previous.get(cursor)!
				path.unshift({ zoneId: cursor, connection: leg.connection })
				cursor = leg.zoneId
			}
			path.unshift({ zoneId: startZoneId })
			return path
		}
		for (const edge of adjacency.get(current) ?? []) {
			if (visited.has(edge.to)) continue
			visited.add(edge.to)
			previous.set(edge.to, { zoneId: current, connection: edge.connection })
			queue.push(edge.to)
		}
	}
	return null
}

function runLengthLabel(zoneCount: number) {
	if (zoneCount <= 3) return { label: 'Short run', className: 'bg-green-200 text-green-900 dark:bg-green-900 dark:text-green-200' }
	if (zoneCount <= 6) return { label: 'Medium run', className: 'bg-yellow-200 text-yellow-900 dark:bg-yellow-900 dark:text-yellow-200' }
	return { label: 'Long haul', className: 'bg-red-200 text-red-900 dark:bg-red-900 dark:text-red-200' }
}

export default function DungeonRuns() {
	const [raceId, setRaceId] = useState<string | null>(null)
	const [cityZoneId, setCityZoneId] = useState<string | null>(null)

	const race = races.find((r) => r.id === raceId) ?? null
	const startZoneId =
		race && race.startingCities.length === 1
			? race.startingCities[0]!.zoneId
			: cityZoneId

	const routes = useMemo(() => {
		if (!startZoneId) return []
		return dungeons
			.map((dungeon) => ({
				dungeon,
				path: findPath(startZoneId, dungeon.id),
			}))
			.sort(
				(a, b) => (a.path?.length ?? Infinity) - (b.path?.length ?? Infinity),
			)
	}, [startZoneId])

	return (
		<div className="container mx-auto text-black dark:text-white">
			<section id="dungeon-runs">
				<h1 className="text-3xl px-10 mb-4 text-cyan-900 dark:text-cyan-200">
					<Link to="/gaming" className="underline">Gaming</Link>
					{' - '}
					<Link to="/gaming/everquest-legends" className="underline">EverQuest Legends</Link>
					{' - Dungeon Runs'}
				</h1>

				<div className="px-10 mb-8 space-y-4">
					<p className="text-xl">
						The project: make a brand new character and run it to a dungeon —
						no ports, no help, just my legs and whatever aggros along the way.
						Live on stream, recorded for the channel.
					</p>
					<p>
						Pick a race below to see where you start and the zone-by-zone path
						to each dungeon. Routes are seeded from classic Norrath geography
						and get corrected as I actually run them in EverQuest Legends.
					</p>
				</div>

				<div className="px-10 mb-8">
					<h2 className="text-2xl font-bold mb-4">1. Choose your race</h2>
					<div className="flex flex-wrap gap-3">
						{races.map((r) => (
							<button
								key={r.id}
								onClick={() => {
									setRaceId(r.id)
									setCityZoneId(null)
								}}
								className={`px-4 py-2 rounded-full shadow duration-200 hover:opacity-80 ${
									r.id === raceId
										? 'bg-cyan-600 dark:bg-cyan-800 text-white'
										: 'bg-gray-200 dark:bg-gray-700'
								}`}
							>
								{r.name}
							</button>
						))}
					</div>
				</div>

				{race && race.startingCities.length > 1 && (
					<div className="px-10 mb-8">
						<h2 className="text-2xl font-bold mb-4">
							2. {race.name}s can start in more than one city — pick one
						</h2>
						<div className="flex flex-wrap gap-3">
							{race.startingCities.map((city) => (
								<button
									key={city.zoneId}
									onClick={() => setCityZoneId(city.zoneId)}
									className={`px-4 py-2 rounded-full shadow duration-200 hover:opacity-80 ${
										city.zoneId === cityZoneId
											? 'bg-cyan-600 dark:bg-cyan-800 text-white'
											: 'bg-gray-200 dark:bg-gray-700'
									}`}
								>
									{city.label}
								</button>
							))}
						</div>
					</div>
				)}

				{race && startZoneId && (
					<div className="px-10 mb-8">
						<h2 className="text-2xl font-bold mb-2">
							Dungeon routes from{' '}
							{race.startingCities.find((c) => c.zoneId === startZoneId)?.label ??
								zoneName(startZoneId)}
						</h2>
						<p className="mb-6 text-gray-600 dark:text-gray-400">
							Sorted from closest to farthest. Zone counts include the city and
							the dungeon itself.
						</p>
						<div className="grid gap-6 md:grid-cols-2">
							{routes.map(({ dungeon, path }) => {
								const run = recordedRuns.find(
									(r) => r.raceId === race.id && r.dungeonId === dungeon.id,
								)
								const length = path ? runLengthLabel(path.length) : null
								return (
									<div
										key={dungeon.id}
										className="rounded-2xl border border-gray-300 dark:border-gray-700 p-5 shadow"
									>
										<div className="flex items-start justify-between gap-2 mb-1">
											<h3 className="text-xl font-bold">{dungeon.name}</h3>
											{length && (
												<span className={`text-sm px-3 py-1 rounded-full whitespace-nowrap ${length.className}`}>
													{length.label}
												</span>
											)}
										</div>
										<p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
											Levels {dungeon.levelRange} — {dungeon.blurb}
										</p>
										{path ? (
											<ol className="space-y-1">
												{path.map((leg, index) => (
													<li key={leg.zoneId} className="flex flex-col">
														<span>
															{index === 0 ? '🏠 ' : index === path.length - 1 ? '⚔️ ' : '→ '}
															{zoneName(leg.zoneId)}
														</span>
														{leg.connection?.note && (
															<span className="ml-6 text-sm italic text-cyan-800 dark:text-cyan-300">
																🚢 {leg.connection.note}
															</span>
														)}
														{leg.connection?.danger && (
															<span className="ml-6 text-sm italic text-red-700 dark:text-red-400">
																⚠️ {leg.connection.danger}
															</span>
														)}
													</li>
												))}
											</ol>
										) : (
											<p className="italic">
												No known land route — this one needs research on stream.
											</p>
										)}
										{run && (
											<p className="mt-3">
												<a
													href={run.youtubeUrl}
													className="underline text-cyan-800 dark:text-cyan-300"
												>
													▶ Watch the run: {run.title}
												</a>
											</p>
										)}
									</div>
								)
							})}
						</div>
					</div>
				)}

				{!race && (
					<p className="px-10 mb-8 text-center italic font-bold">
						Pick a race above to see your dungeon routes.
					</p>
				)}
			</section>

			<div className="flex flex-wrap justify-center gap-8 rounded-3xl py-4">
				{WatchCTA()}
			</div>
		</div>
	)
}
